import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import StatusCard from "@/components/StatusCard";
import CustomFormDialog from "@/components/CustomFormDialog";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { useDispatch, useSelector } from "react-redux";
import {
  createApplicationServiceRequest,
  deleteApplicationServiceRequest,
  getApplicationsServiceRequest,
} from "@/services/applications/actions";
import { useNavigate } from "react-router-dom";

export default function ApplicationManagement() {
  const [currentPage, setCurrentPage] = useState(1);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newApp, setNewApp] = useState({
    name: "",
    app_site: "",
    api_url: "",
  });
  const [formErrors, setFormErrors] = useState({
    name: "",
    app_site: "",
    api_url: "",
  });
  const [alert, setAlert] = useState({ show: false, type: "", message: "" });
  const itemsPerPage = 3;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const applicationsState = useSelector((state) => state.applications);
  const applications = applicationsState?.applications || [];
  const loading = applicationsState?.loading;
  const error = applicationsState?.error;

  useEffect(() => {
    dispatch(getApplicationsServiceRequest());
  }, [dispatch]);

  useEffect(() => {
    if (alert.show) {
      const timer = setTimeout(() => {
        setAlert({ show: false, type: "", message: "" });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [alert.show]);

  useEffect(() => {
    const totalPages = Math.ceil(applications.length / itemsPerPage);
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [applications, currentPage, itemsPerPage]);

  const handleView = (app) => {
    navigate(`/applicationmanagement/${app.id}/users`);
  };

  const handleDelete = (app) => {
    dispatch(deleteApplicationServiceRequest(app.id));
    setAlert({
      show: true,
      type: "success",
      message: `${app.name} başarıyla silindi.`,
    });
  };

  const handleAddApp = () => {
    const errors = {
      name: !newApp.name ? "Uygulama adı zorunludur" : "",
      app_site: !newApp.app_site ? "Uygulama sitesi zorunludur" : "",
      api_url: !newApp.api_url ? "API URL zorunludur" : "",
    };
    setFormErrors(errors);
    if (Object.values(errors).some((v) => v)) return;
    dispatch(
      createApplicationServiceRequest({
        name: newApp.name,
        app_site: newApp.app_site,
        api_url: newApp.api_url,
      })
    );
    setIsDialogOpen(false);
    setNewApp({ name: "", app_site: "", api_url: "" });
    setFormErrors({ name: "", app_site: "", api_url: "" });
    setAlert({
      show: true,
      type: "success",
      message: `Yeni uygulama başarıyla eklendi.`,
    });
  };

  const totalPages = Math.ceil(applications.length / itemsPerPage);
  const paginatedData = applications.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="p-6 w-full min-w-0">
      <div className="flex items-center gap-3 mb-6 justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">Uygulama Yönetimi</h1>
        </div>
        <Button variant="default" onClick={() => setIsDialogOpen(true)}>
          Yeni Uygulama Ekle
        </Button>
      </div>

      {alert.show && (
        <Alert
          variant={alert.type === "success" ? "success" : "destructive"}
          className="mb-4"
        >
          <AlertTitle>
            {alert.type === "success" ? "Başarılı" : "Hata"}
          </AlertTitle>
          <AlertDescription>{alert.message}</AlertDescription>
        </Alert>
      )}

      <CustomFormDialog
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        title="Yeni Uygulama Ekle"
        description="Yeni bir uygulama eklemek için aşağıdaki formu doldurun."
        fields={[
          { name: "name", label: "Uygulama Adı", required: true },
          { name: "app_site", label: "Uygulama Sitesi", required: true },
          { name: "api_url", label: "API URL", required: true },
        ]}
        formData={newApp}
        setFormData={setNewApp}
        formErrors={formErrors}
        setFormErrors={setFormErrors}
        onSubmit={handleAddApp}
        submitLabel="Ekle"
        cancelLabel="İptal"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-0">
        {paginatedData.map((app) => (
          <StatusCard
            key={app.id}
            title={app.name}
            description={app.app_site}
            status={app.status || "Pasif"}
            statusType="badge"
            statusColor={app.status === "Aktif" ? "green" : "red"}
            additionalInfo={[
              {
                label: "API URL",
                value: app.api_url || "Belirtilmemiş",
              },
              {
                label: "Son Güncelleme",
                value: new Date().toLocaleDateString("tr-TR"),
              },
            ]}
            actions={[
              {
                icon: <Button variant="show" size="icon" />,
                onClick: () => handleView(app),
              },
              {
                icon: <Button variant="trash" size="icon" />,
                onClick: () => handleDelete(app),
              },
            ]}
          />
        ))}
      </div>

      {totalPages > 0 && (
        <div className="mt-6 flex justify-center w-full">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  className={
                    currentPage === 1 ? "pointer-events-none opacity-50" : ""
                  }
                />
              </PaginationItem>
              {[...Array(totalPages)].map((_, i) => (
                <PaginationItem key={`page-${i}`}>
                  <PaginationLink
                    href="#"
                    isActive={currentPage === i + 1}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={() =>
                    setCurrentPage(Math.min(totalPages, currentPage + 1))
                  }
                  className={
                    currentPage === totalPages
                      ? "pointer-events-none opacity-50"
                      : ""
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}
